<script>
import { computed, ref } from 'vue';
import { useDropdownPosition } from 'dashboard/composables/useDropdownPosition';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import FileUpload from 'vue-upload-component';
import * as ActiveStorage from 'activestorage';
import inboxMixin from 'shared/mixins/inboxMixin';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import { getAllowedFileTypesByChannel } from '@chatwoot/utils';
import VideoCallButton from '../VideoCallButton.vue';
import { INBOX_TYPES } from 'dashboard/helper/inbox';
import { mapGetters } from 'vuex';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  name: 'ReplyBottomPanel',
  components: { NextButton, FileUpload, VideoCallButton },
  mixins: [inboxMixin],
  props: {
    showEmojiPicker: { type: Boolean, default: false },
    compact: {
      type: Boolean,
      default: false,
    },
    isNote: {
      type: Boolean,
      default: false,
    },
    onSend: {
      type: Function,
      default: () => {},
    },
    sendButtonText: {
      type: String,
      default: '',
    },
    recordingAudioDurationText: {
      type: String,
      default: '00:00',
    },
    // inbox prop is used in /mixins/inboxMixin,
    // remove this props when refactoring to composable if not needed
    // eslint-disable-next-line vue/no-unused-properties
    inbox: {
      type: Object,
      default: () => ({}),
    },
    showFileUpload: {
      type: Boolean,
      default: false,
    },
    showAudioRecorder: {
      type: Boolean,
      default: false,
    },
    onFileUpload: {
      type: Function,
      default: () => {},
    },
    toggleEmojiPicker: {
      type: Function,
      default: () => {},
    },
    toggleAudioRecorder: {
      type: Function,
      default: () => {},
    },
    toggleAudioRecorderPlayPause: {
      type: Function,
      default: () => {},
    },
    isRecordingAudio: {
      type: Boolean,
      default: false,
    },
    recordingAudioState: {
      type: String,
      default: '',
    },
    isSendDisabled: {
      type: Boolean,
      default: false,
    },
    isOnPrivateNote: {
      type: Boolean,
      default: false,
    },
    enableMultipleFileUpload: {
      type: Boolean,
      default: true,
    },
    enableWhatsAppTemplates: {
      type: Boolean,
      default: false,
    },
    enableContentTemplates: {
      type: Boolean,
      default: false,
    },
    enableWhatsappFlow: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: Number,
      required: true,
    },
    // eslint-disable-next-line vue/no-unused-properties
    message: {
      type: String,
      default: '',
    },
    newConversationModalActive: {
      type: Boolean,
      default: false,
    },
    portalSlug: {
      type: String,
      required: true,
    },
    conversationType: {
      type: String,
      default: '',
    },
    showQuotedReplyToggle: {
      type: Boolean,
      default: false,
    },
    quotedReplyEnabled: {
      type: Boolean,
      default: false,
    },
    isEditorDisabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    'toggleInsertArticle',
    'selectWhatsappTemplate',
    'selectContentTemplate',
    'selectWhatsappFlow',
    'toggleQuotedReply',
  ],
  setup(props) {
    const uploadRef = ref(false);
    const emojiTrigger = ref(null);
    const emojiPopup = ref(null);
    const { fixedPosition: emojiPosition } = useDropdownPosition(
      emojiTrigger,
      emojiPopup,
      computed(() => props.showEmojiPicker)
    );

    const keyboardEvents = {
      '$mod+Alt+KeyA': {
        action: () => {
          // Skip if editor is disabled (e.g., WhatsApp 24-hour window expired)
          if (props.isEditorDisabled) return;

          // TODO: This is really hacky, we need to replace the file picker component with
          // a custom one, where the logic and the component markup is isolated.
          // Once we have the custom component, we can remove the hacky logic below.

          const uploadTriggerButton = document.querySelector(
            '#conversationAttachment'
          );
          if (uploadTriggerButton) uploadTriggerButton.click();
        },
        allowOnFocusedInput: true,
      },
    };

    useKeyboardEvents(keyboardEvents);

    return {
      uploadRef,
      emojiTrigger,
      emojiPopup,
      emojiPosition,
    };
  },
  computed: {
    showVideoCallButton() {
      return (
        (this.isAWebWidgetInbox || this.isAPIInbox) &&
        !this.isOnPrivateNote &&
        !this.isEditorDisabled
      );
    },
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      uiFlags: 'integrations/getUIFlags',
    }),
    wrapClass() {
      return {
        'is-note-mode': this.isNote,
        'is-restricted': this.isEditorDisabled,
        'is-compact': this.compact,
      };
    },
    showAttachButton() {
      if (this.isEditorDisabled) return false;
      return this.showFileUpload || this.isNote;
    },
    showAudioRecorderButton() {
      if (this.isEditorDisabled) return false;
      if (this.isALineChannel || this.isATiktokChannel) {
        return false;
      }
      // Disable audio recorder for safari browser as recording is not supported
      // const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(
      //   navigator.userAgent
      // );

      return (
        this.isFeatureEnabledonAccount(
          this.accountId,
          FEATURE_FLAGS.VOICE_RECORDER
        ) && this.showAudioRecorder
        // !isSafari
      );
    },
    showAudioPlayStopButton() {
      if (this.isEditorDisabled) return false;
      return this.showAudioRecorder && this.isRecordingAudio;
    },
    isInstagramDM() {
      return this.conversationType === 'instagram_direct_message';
    },
    allowedFileTypes() {
      if (this.isOnPrivateNote) {
        return getAllowedFileTypesByChannel();
      }

      let channelType = this.channelType || this.inbox?.channel_type;
      if (this.isAnInstagramChannel || this.isInstagramDM) {
        channelType = INBOX_TYPES.INSTAGRAM;
      }

      return getAllowedFileTypesByChannel({
        channelType,
        medium: this.inbox?.medium,
      });
    },
    enableDragAndDrop() {
      return !this.newConversationModalActive;
    },
    audioRecorderPlayStopIcon() {
      switch (this.recordingAudioState) {
        // playing paused recording stopped inactive destroyed
        case 'playing':
          return 'i-ph-pause';
        case 'paused':
          return 'i-ph-play';
        case 'stopped':
          return 'i-ph-play';
        default:
          return 'i-ph-stop';
      }
    },
    enableInsertArticleInReply() {
      return this.portalSlug;
    },
    isFetchingAppIntegrations() {
      return this.uiFlags.isFetching;
    },
    quotedReplyToggleTooltip() {
      return this.quotedReplyEnabled
        ? this.$t('CONVERSATION.REPLYBOX.QUOTED_REPLY.DISABLE_TOOLTIP')
        : this.$t('CONVERSATION.REPLYBOX.QUOTED_REPLY.ENABLE_TOOLTIP');
    },
    whatsappTemplateButtonLabel() {
      return this.isEditorDisabled
        ? this.$t('CONVERSATION.FOOTER.SEND_WHATSAPP_TEMPLATE')
        : '';
    },
  },
  mounted() {
    ActiveStorage.start();
  },
  methods: {
    toggleInsertArticle() {
      this.$emit('toggleInsertArticle');
    },
  },
};
</script>

<template>
  <div class="reply-bottom-panel" :class="wrapClass">
    <div class="left-wrap">
      <div
        v-if="!isEditorDisabled"
        ref="emojiTrigger"
        class="composer-emoji flex shrink-0"
      >
        <NextButton
          v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON')"
          icon="i-hugeicons-smile"
          slate
          faded
          sm
          @click="toggleEmojiPicker"
        />
        <Teleport to="body">
          <div
            v-if="showEmojiPicker"
            ref="emojiPopup"
            class="w-[22rem] max-w-[calc(100vw-2rem)]"
            :class="emojiPosition.class"
            :style="emojiPosition.style"
          >
            <slot name="emoji-picker" />
          </div>
        </Teleport>
      </div>
      <FileUpload
        v-if="showAttachButton"
        ref="uploadRef"
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
        input-id="conversationAttachment"
        :size="4096 * 4096"
        :accept="allowedFileTypes"
        :multiple="enableMultipleFileUpload"
        :drop="enableDragAndDrop"
        :drop-directory="false"
        :data="{
          direct_upload_url: '/rails/active_storage/direct_uploads',
          direct_upload: true,
        }"
        @input-file="onFileUpload"
      >
        <NextButton
          v-if="showAttachButton"
          v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
          icon="i-ph-paperclip"
          slate
          faded
          sm
        />
      </FileUpload>
      <NextButton
        v-if="showAudioRecorderButton"
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_AUDIORECORDER_ICON')"
        class="composer-microphone"
        :icon="
          !isRecordingAudio ? 'i-hugeicons-mic-01' : 'i-hugeicons-mic-off-01'
        "
        :aria-label="$t('CONVERSATION.REPLYBOX.TIP_AUDIORECORDER_ICON')"
        slate
        faded
        sm
        @click="toggleAudioRecorder"
      />
      <NextButton
        v-if="showAudioPlayStopButton"
        :icon="audioRecorderPlayStopIcon"
        slate
        faded
        sm
        :label="recordingAudioDurationText"
        @click="toggleAudioRecorderPlayPause"
      />
      <div class="composer-extra-actions">
        <NextButton
          v-if="showQuotedReplyToggle"
          v-tooltip.top-end="quotedReplyToggleTooltip"
          icon="i-ph-quotes"
          :variant="quotedReplyEnabled ? 'solid' : 'faded'"
          color="slate"
          sm
          :aria-pressed="quotedReplyEnabled"
          @click="$emit('toggleQuotedReply')"
        />
        <NextButton
          v-if="enableWhatsAppTemplates"
          v-tooltip.top-end="$t('CONVERSATION.FOOTER.WHATSAPP_TEMPLATES')"
          icon="i-ph-whatsapp-logo"
          :label="whatsappTemplateButtonLabel"
          :color="isEditorDisabled ? 'blue' : 'slate'"
          :variant="isEditorDisabled ? 'solid' : 'faded'"
          :size="isEditorDisabled ? 'md' : 'sm'"
          :class="{ 'restricted-template-button': isEditorDisabled }"
          @click="$emit('selectWhatsappTemplate')"
        />
        <NextButton
          v-if="enableWhatsappFlow"
          v-tooltip.top-end="
            isEditorDisabled
              ? $t('WHATSAPP_FLOWS.SEND.WINDOW')
              : $t('WHATSAPP_FLOWS.SEND.ACTION_TOOLTIP')
          "
          icon="i-hugeicons-workflow-square-06"
          :aria-label="$t('WHATSAPP_FLOWS.SEND.ACTION')"
          slate
          faded
          sm
          @click="$emit('selectWhatsappFlow')"
        />
        <NextButton
          v-if="enableContentTemplates"
          v-tooltip.top-end="'Content Templates'"
          icon="i-ph-whatsapp-logo"
          slate
          faded
          sm
          @click="$emit('selectContentTemplate')"
        />
        <VideoCallButton
          v-if="showVideoCallButton"
          :conversation-id="conversationId"
        />
        <transition name="modal-fade">
          <div
            v-show="uploadRef && uploadRef.dropActive"
            class="flex fixed top-0 right-0 bottom-0 left-0 z-20 flex-col gap-2 justify-center items-center w-full h-full text-n-slate-12 bg-modal-backdrop-light dark:bg-modal-backdrop-dark"
          >
            <fluent-icon icon="cloud-backup" size="40" />
            <h4 class="text-2xl break-words text-n-slate-12">
              {{ $t('CONVERSATION.REPLYBOX.DRAG_DROP') }}
            </h4>
          </div>
        </transition>
        <NextButton
          v-if="enableInsertArticleInReply"
          v-tooltip.top-end="
            $t('HELP_CENTER.ARTICLE_SEARCH.OPEN_ARTICLE_SEARCH')
          "
          icon="i-ph-article-ny-times"
          slate
          faded
          sm
          @click="toggleInsertArticle"
        />
      </div>
    </div>
    <div class="right-wrap">
      <NextButton
        v-if="!isEditorDisabled"
        :label="compact ? '' : sendButtonText"
        :icon="compact ? 'i-hugeicons-sent' : undefined"
        :aria-label="sendButtonText"
        :title="sendButtonText"
        type="submit"
        sm
        :color="isNote ? 'amber' : 'blue'"
        :disabled="isSendDisabled"
        class="flex-shrink-0"
        @click="onSend"
      />
    </div>
  </div>
</template>

<style lang="scss" scoped>
.composer-extra-actions {
  @apply flex items-center gap-0.5;
}

.reply-bottom-panel.is-compact {
  @apply items-center gap-1 p-0;

  .left-wrap {
    @apply border-0 bg-transparent p-0 shadow-none;
  }

  .composer-emoji {
    @apply absolute bottom-3 left-2;
  }

  .right-wrap :deep(button) {
    @apply size-10 rounded-xl p-0;
  }
}

@media (max-width: 479px) {
  .reply-bottom-panel.is-compact {
    @apply justify-between;

    .composer-emoji {
      @apply static order-first;
    }
  }
}

.left-wrap {
  @apply flex items-center gap-0.5 rounded-xl border border-n-weak bg-n-alpha-1 p-1 shadow-sm;
}

.left-wrap :deep(button:not(.restricted-template-button)) {
  @apply size-8 min-w-8 rounded-lg bg-transparent p-0 text-n-slate-10 shadow-none transition-colors hover:enabled:bg-n-alpha-2 hover:enabled:text-n-slate-12;
}

.left-wrap :deep(button:not(.restricted-template-button)[aria-pressed='true']) {
  @apply bg-n-blue-3 text-n-blue-10;
}

.right-wrap {
  @apply flex;
}

:deep(.file-uploads) {
  label {
    @apply cursor-pointer;
  }

  &:hover button:not(:disabled) {
    @apply bg-n-alpha-2 text-n-slate-12;
  }
}

.reply-bottom-panel {
  @apply flex justify-between gap-2 bg-transparent px-3 pb-2 pt-1.5;
}

.reply-bottom-panel.is-note-mode .left-wrap {
  @apply border-n-amber-7 bg-n-amber-2;
}

.reply-bottom-panel.is-note-mode
  .left-wrap
  :deep(button:not(.restricted-template-button)) {
  @apply text-n-amber-12 hover:enabled:bg-n-amber-3 hover:enabled:text-n-amber-12;
}

.reply-bottom-panel.is-restricted {
  @apply justify-start border-t-0 bg-transparent px-4 pb-4 pt-1;
}

.reply-bottom-panel.is-restricted .left-wrap {
  @apply w-full border-0 bg-transparent p-0 shadow-none;
}

.restricted-template-button {
  @apply w-full justify-center shadow-sm shadow-n-brand/20;
}

@media (max-width: 767px) {
  .reply-bottom-panel {
    @apply p-2 items-end;
  }

  .left-wrap {
    @apply flex-wrap gap-0.5 rounded-lg;
  }

  .right-wrap {
    @apply flex-shrink-0;
  }

  .right-wrap :deep(button) {
    max-width: 9.25rem;
  }

  .right-wrap :deep(.button__content),
  .right-wrap :deep(.button__label) {
    @apply truncate;
  }
}
</style>
